import { useMemo, useEffect } from 'react';
import {
  NavLink,
  useParams,
  Outlet,
  useLocation,
  generatePath,
} from 'react-router-dom';
import {
  Table,
  useQueryState,
  useFilters,
  useGetList,
  IconExpand,
  formatDate,
  SelectItemProps,
  PermissionEnum,
  usePrevious,
  PermissionDenied,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  linkedClientsColumnsItems,
  linkedClientsColumnsLabels,
  linkedClientsFiltersSchema,
  linkedClientsSchema,
} from './variables/linked-clients';
import { useLazyGetCustomersExternalOrganizationsQuery } from '../store/services/customers';
import {
  CustomerStatus,
  LinkedClient,
  OrganizationType,
  PspIntegrationPayment,
} from '../types';

import ROUTES from '../../../routes';

import cn from '../customers.module.css';
import { clearLinkedClientsState } from '../store/slices/external-accounts';

const formatLinkedClientsList = (
  list?: LinkedClient[],
  pspPartners?: SelectItemProps[],
): LinkedClient[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    let currentPspPartner = <div>-</div>;

    if (result.pspPartnerId) {
      currentPspPartner = (
        <NavLink
          to={`/bank-and-psp/${result.pspPartnerId}`}
          target="_blank"
          className={cn.linkedClientsPspPartnerLink}
        >
          {
            pspPartners?.find(
              (pspPartner) => pspPartner.value === result.pspPartnerId,
            )?.name
          }
        </NavLink>
      );
    }

    const render: Record<string, any> = {
      createdAt: <div>{formatDate(result?.createdAt)}</div>,
      pspPartnerId: currentPspPartner,
      active: <div>{result.active ? 'Active' : 'Inactive'}</div>,
    };

    result.render = render;

    return result;
  });

  return nextList as LinkedClient[];
};

const CustomerLinkedClients = ({ type }: { type: OrganizationType }) => {
  const { customerId } = useParams();
  const dispatch = useAppDispatch();

  const { baseInformation: corporateBaseInformation } = useAppSelector(
    (state) => state.corporateCustomers.info.general,
  );
  const { baseInformation: individualBaseInformation } = useAppSelector(
    (state) => state.individualCustomers.info.general,
  );
  const {
    pspPartners,
    pspIntegrations,
    linkedClients,
    linkedClientsPagination,
  } = useAppSelector((state) => state.customersExternalAccounts);
  const { activePermissions } = useAppSelector(
    (state) => state.customersDictionaries,
  );

  const [
    getCustomersExternalOrganizations,
    { isLoading: linkedClientsIsLoading, isFetching: linkedClientsIsFetching },
  ] = useLazyGetCustomersExternalOrganizationsQuery();

  const organizationStatus =
    type === OrganizationType.INDIVIDUAL
      ? individualBaseInformation?.status
      : corporateBaseInformation?.status;

  const { filters, onSortChange, onFilterChange } =
    useQueryState(linkedClientsSchema);

  const prevFilters = usePrevious(filters);

  const { search } = useLocation();

  const { getListAction, onFilter, setPage } = useGetList<LinkedClient>({
    getList: getCustomersExternalOrganizations,
    onSortChange,
    onFilterChange,
    schema: linkedClientsSchema,
    itemId: customerId,
  });

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema: linkedClientsFiltersSchema,
    filters,
    onFilterChange,
  });

  const linkedClientsFormattedData = useMemo(() => {
    const data = formatLinkedClientsList(
      linkedClients,
      pspPartners?.map((item) => ({
        name: item.companyName,
        value: item.id,
      })),
    );

    return data;
  }, [pspPartners, linkedClients]);

  useEffect(() => {
    if (pspPartners && pspIntegrations) {
      const nextPspPartners = pspPartners.map((item) => {
        const result = { ...item };

        if (
          result.pspIntegrationId &&
          pspIntegrations?.find(
            (integration) => integration.id === result.pspIntegrationId,
          )
        ) {
          const currentItegration = pspIntegrations?.find(
            (integration) => integration.id === result.pspIntegrationId,
          );

          if (currentItegration) {
            result.pspIntegration = {
              name: currentItegration.name,
              pspIntegrationPayments:
                currentItegration.pspIntegrationPayments as PspIntegrationPayment[],
              id: currentItegration.id,
              externalCustomersSupported:
                currentItegration.externalCustomersSupported,
            };
          }
        }

        return result;
      });

      const pspPartnersWithExternalCustomerSupport = nextPspPartners?.filter(
        (item) => item?.pspIntegration?.externalCustomersSupported,
      );

      if (pspPartnersWithExternalCustomerSupport?.length) {
        setValues(
          'pspPartnerId',
          pspPartnersWithExternalCustomerSupport?.map((item) => ({
            name: item.companyName,
            value: item.id,
          })),
        );
      }
    }
  }, [pspPartners, pspIntegrations]);

  useEffect(() => {
    if (
      !linkedClients &&
      !search &&
      !Object.keys(filters).length &&
      customerId &&
      activePermissions?.includes(
        PermissionEnum.CUSTOMERS_EXTERNAL_ORGANIZATIONS_LIST,
      )
    ) {
      getListAction({});
    }
  }, [filters, search, linkedClients, customerId, activePermissions]);

  useEffect(() => {
    if (
      customerId &&
      !linkedClients &&
      search &&
      filters &&
      prevFilters &&
      Object.keys(prevFilters).length &&
      activePermissions?.includes(
        PermissionEnum.CUSTOMERS_EXTERNAL_ORGANIZATIONS_LIST,
      )
    ) {
      const filtersValues: any = {
        ...filters,
      };

      getListAction({ filters: filtersValues });
    }
  }, [
    filters,
    search,
    linkedClients,
    prevFilters,
    customerId,
    activePermissions,
  ]);

  useEffect(
    () => () => {
      dispatch(clearLinkedClientsState());
    },
    [],
  );

  const link =
    type === OrganizationType.INDIVIDUAL
      ? ROUTES.CUSTOMERS_INDIVIDUAL_EXTERNAL_LINKED_CLIENTS_CREATE
      : ROUTES.CUSTOMERS_CORPORATE_EXTERNAL_LINKED_CLIENTS_CREATE;

  let content;

  if (
    !activePermissions?.includes(
      PermissionEnum.CUSTOMERS_EXTERNAL_ORGANIZATIONS_LIST,
    )
  ) {
    content = <PermissionDenied />;
  } else {
    content = (
      <>
        {activePermissions?.includes(
          PermissionEnum.EXTERNAL_ORGANIZATIONS_CREATE_ACTION,
        ) &&
          organizationStatus !== CustomerStatus.CLOSED && (
            <div className={cn.externalAddLinkWrapper}>
              <NavLink
                to={`${generatePath(link, {
                  customerId: (customerId as string) || '',
                })}${search || ''}`}
                className={cn.externalAddLink}
              >
                <IconExpand width={16} height={16} />
                Link Customer
              </NavLink>
            </div>
          )}
        <div className={cn.externalAccountsTable}>
          <Table
            columns={linkedClientsColumnsItems}
            data={linkedClientsFormattedData || []}
            onSort={() => {}}
            sort={{}}
            filters={filters}
            columnsLabels={linkedClientsColumnsLabels}
            onFilterChange={(values) => onFilter(values)}
            filtersComponent={filtersComponent}
            handleSubmit={handleSubmit}
            isLoading={linkedClientsIsLoading || linkedClientsIsFetching}
            pagination={linkedClientsPagination}
            tableBodyClassName={
              linkedClientsIsLoading || linkedClientsIsFetching
                ? cn.tableLoading
                : ''
            }
            take="20"
            tableTopClassName={
              Object.keys(filters)?.length ? cn.externalWithPadding : ''
            }
            setPage={(data) => {
              setPage({
                ...data,
                filters,
              });
            }}
          />
        </div>
        <Outlet />
      </>
    );
  }

  return content;
};

export default CustomerLinkedClients;
